import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import LegendItem from './LegendItem.tsx';
import ChartCard from './ChartCard.tsx';
import Chart from './Chart.tsx';
import { aggregateByMonth, formatDate, formatDateFull, getTicks, secondsToHumanReadable } from '../utils/chartUtils.ts';
import TooltipContent from './TooltipContent.tsx';
import TextChart from './TextChart.tsx';

interface ChartProps {
    data: any[];
    [key: string]: any;
}

const TimeSpentAssistingDocumentsCard = ({
    data,
    ...props
}: Omit<React.ComponentPropsWithRef<typeof ChartCard>, 'title' | 'helpText' | 'controls'> & { data: any }) => {
    const { t } = useTranslation('analytics');

    const isDataValid = data != null && Array.isArray(data) && data.length > 0;

    return (
        <ChartCard
            title={t('dashboard.charts.timeSpentAssistingDocumentsCard.title')}
            helpText={t('dashboard.charts.timeSpentAssistingDocumentsCard.helpText')}
            note={t('dashboard.charts.assistanceTimeRetentionNote')}
            {...props}
        >
            {isDataValid && <TimeSpentAssistingDocumentsChart data={data} />}
        </ChartCard>
    );
};

export default TimeSpentAssistingDocumentsCard;

const TimeSpentAssistingDocumentsChart = ({ data }: ChartProps) => {
    const { t, i18n } = useTranslation('analytics');

    const hasAssistanceTimeData = data?.some((item) => item.totalAssistanceTime != null);

    const dayCount = data.length;
    const isMonthly = dayCount > 32;
    const processedData = isMonthly
        ? aggregateByMonth(data, ['totalAssistanceTime', 'totalAssistanceTimeBeforeWorkist'])
        : data;
    const height = 400;
    const barSize = Math.max(20, Math.min(80, processedData.length <= 7 ? 60 : processedData.length <= 15 ? 40 : 30));

    const maxValue = Math.max(
        ...processedData.map((d) => Math.max(d.totalAssistanceTime || 0, d.totalAssistanceTimeBeforeWorkist || 0))
    );
    const timeUnit = maxValue > 86400 ? 'd' : maxValue > 3600 ? 'h' : maxValue > 60 ? 'min' : 's';

    // Round up to the nearest hour if the max value is greater than 60 minutes
    const factor = maxValue > 86400 ? 86400 : maxValue > 3600 ? 3600 : maxValue > 60 ? 60 : 1;
    const maxDisplayValue = Math.ceil(maxValue / factor) * factor;

    if (!hasAssistanceTimeData) {
        return <TextChart value={t('dashboard.charts.noDataAvailable')} />;
    }

    return (
        <Chart>
            <Chart.Legend>
                <LegendItem className="text-content-medium" color="data1">
                    {t('dashboard.charts.timeSpentAssistingDocumentsCard.withWorkist')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data2">
                    {t('dashboard.charts.timeSpentAssistingDocumentsCard.withoutWorkist')}
                </LegendItem>
            </Chart.Legend>

            <Chart.Content>
                <Chart.FixedSide>
                    <BarChart
                        data={processedData}
                        margin={{ top: 0, left: 0, right: 0, bottom: 10 }}
                        width={70}
                        height={height}
                    >
                        <XAxis
                            dataKey="date"
                            width={0}
                            axisLine={false}
                            tickLine={false}
                            fill="transparent"
                            stroke="transparent"
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            className="text-xs fill-content-strong"
                            domain={[0, maxDisplayValue]}
                            allowDecimals={false}
                            tickFormatter={(value) => {
                                const [formattedValue, unit] = secondsToHumanReadable(value, timeUnit, i18n.language);
                                const translatedUnit = t(`dashboard.timeUnitsShort.${unit}`);
                                return `${formattedValue} ${translatedUnit}`;
                            }}
                            ticks={getTicks(5, maxDisplayValue)}
                        />
                        <Bar dataKey="automated" fill="transparent" stroke="transparent" />
                    </BarChart>
                </Chart.FixedSide>

                <Chart.Scrollable>
                    <ResponsiveContainer
                        width={processedData.length < 10 ? '100%' : processedData.length * 120}
                        height={height}
                    >
                        <BarChart
                            data={processedData}
                            margin={{ right: 30, bottom: 10 }}
                            width={processedData.length * 120}
                            height={height}
                        >
                            <CartesianGrid vertical={false} stroke="inherit" className="stroke-line-weak" />
                            <RechartsTooltip
                                cursor={<Chart.TooltipCursor />}
                                content={({ payload }) => {
                                    const date = payload?.[0]?.payload.date;
                                    const formattedDate = date && formatDateFull(i18n.language, date, isMonthly);

                                    const [withWorkistFormatted, withWorkistUnit] = secondsToHumanReadable(
                                        payload?.[0]?.payload.totalAssistanceTime || 0,
                                        timeUnit,
                                        i18n.language
                                    );
                                    const [withoutWorkistFormatted, withoutWorkistUnit] = secondsToHumanReadable(
                                        payload?.[0]?.payload.totalAssistanceTimeBeforeWorkist || 0,
                                        timeUnit,
                                        i18n.language
                                    );

                                    const translatedWithWorkistUnit = t(`dashboard.timeUnitsShort.${withWorkistUnit}`);
                                    const translatedWithoutWorkistUnit = t(
                                        `dashboard.timeUnitsShort.${withoutWorkistUnit}`
                                    );

                                    return (
                                        <TooltipContent>
                                            <TooltipContent.Title>{formattedDate}</TooltipContent.Title>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data1">
                                                    {t('dashboard.charts.timeSpentAssistingDocumentsCard.withWorkist')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {`${withWorkistFormatted} ${translatedWithWorkistUnit}`}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data2">
                                                    {t(
                                                        'dashboard.charts.timeSpentAssistingDocumentsCard.withoutWorkist'
                                                    )}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {`${withoutWorkistFormatted} ${translatedWithoutWorkistUnit}`}
                                                </span>
                                            </TooltipContent.Row>
                                        </TooltipContent>
                                    );
                                }}
                            />
                            <XAxis
                                type="category"
                                dataKey="date"
                                tick={{ fill: 'inherit' }}
                                className="text-xs fill-content-medium"
                                tickFormatter={(date) => formatDate(i18n.language, date, isMonthly)}
                                tickMargin={20}
                                minTickGap={20}
                                tickLine={false}
                                axisLine={false}
                                padding={{ left: 20, right: 20 }}
                            />
                            <YAxis domain={[0, maxDisplayValue]} ticks={getTicks(5, maxDisplayValue)} hide />
                            <Bar dataKey="totalAssistanceTime" className="fill-data-data1" barSize={barSize} />
                            <Bar
                                dataKey="totalAssistanceTimeBeforeWorkist"
                                className="fill-data-data2"
                                barSize={barSize}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Chart.Scrollable>
            </Chart.Content>
        </Chart>
    );
};
